/**
 * Post-purchase confirmation banner (#197, FR-12.6.1). The single shared indicator shown
 * on every checkout return surface — `/host` and `/profile` today, any future one for
 * free (it keys off the URL, not the page).
 *
 * On a `?checkout=success` return the credits have already landed via the idempotent
 * payment-completed webhook grant (FR-12.6); this banner is a *confirmation*, never the
 * grant — so a spoofed/absent `credits` param only degrades the copy to a generic
 * "credits added", it can never change the balance. The authoritative amount is the
 * server-stamped `credits` query param (see the checkout handler's `buildReturn`,
 * D197.2), read only for display and never trusted for anything durable.
 *
 * Behavior (owner sign-off A–D, D197.7–D197.11):
 *  - Success → a green "Payment successful" banner in an `aria-live` region, with a
 *    once-animating check badge (no confetti), the exact `+N credits` named when the
 *    param is present, and an explicit × dismiss. No auto-dismiss timer (D197.10).
 *  - Cancel → the existing quiet neutral note (D197.3), also dismissable.
 *  - Neither → renders nothing.
 *  - It self-clears the `checkout`/`credits` params on mount (D197.4) so a later reload of
 *    the same URL does not re-assert a stale "success"; the initial values are captured at
 *    first render so clearing the URL never tears down the banner we're showing.
 *  - All motion is CSS and degrades to a static banner under `prefers-reduced-motion`
 *    (D197.6); the check badge + chip are decorative (`aria-hidden`), the copy carries the
 *    meaning for assistive tech.
 *
 * The *updated balance* the confirmation is tied to (FR-12.6.1) is rendered by the adjacent
 * {@link CreditBalance}, which highlights the same `+N` delta with a one-time pulse — this
 * banner names the amount, the balance display shows the new total.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Parse the server-stamped `credits` param into a positive integer for display, or
 * `undefined` when absent/malformed (→ the banner degrades to generic copy). Never trusted
 * beyond the confirmation text.
 */
export function parseGrantedCredits(raw: string | null): number | undefined {
  // Only a plain run of digits counts — reject `1e3`/`0x14`/`3.5`/whitespace so a crafted
  // URL can never inflate the displayed "+N" via a numeric-literal form. Display-only
  // either way (a bad param just degrades the copy), but this keeps the shown amount an
  // honest echo of the server's stamped integer.
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export function PurchaseSuccess(): React.JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();
  // Capture the return status + amount ONCE (first render). Self-clearing the URL params
  // below must not tear down the banner, so we render from this snapshot, not live params.
  const [initial] = useState(() => ({
    status: searchParams.get('checkout'),
    credits: parseGrantedCredits(searchParams.get('credits')),
  }));
  const [dismissed, setDismissed] = useState(false);

  const isReturn = initial.status === 'success' || initial.status === 'cancel';

  // Strip `checkout`/`credits` after first render (D197.4) so a reload doesn't re-assert
  // success. Runs once — `setSearchParams`'s functional form preserves any other params
  // (e.g. `source=pack` on the host create screen).
  useEffect(() => {
    if (!isReturn) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('checkout');
        next.delete('credits');
        return next;
      },
      { replace: true },
    );
  }, [isReturn, setSearchParams]);

  if (dismissed || !isReturn) return null;

  if (initial.status === 'success') {
    return (
      <div
        className="checkout-banner checkout-banner--success"
        role="status"
        aria-live="polite"
        data-testid="checkout-success"
      >
        <span className="checkout-banner__badge" aria-hidden="true">
          ✓
        </span>
        <div className="checkout-banner__body">
          <p className="checkout-banner__title">Payment successful</p>
          <p className="checkout-banner__detail">
            {initial.credits !== undefined ? (
              <>
                <span
                  className="checkout-banner__chip"
                  data-testid="checkout-success-credits"
                >
                  +{initial.credits} credits
                </span>{' '}
                added to your balance.
              </>
            ) : (
              'Your credits have been added.'
            )}
          </p>
        </div>
        <button
          type="button"
          className="checkout-banner__dismiss"
          data-testid="checkout-success-dismiss"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
        >
          ×
        </button>
      </div>
    );
  }

  // Cancel: quiet neutral note (D197.3), no charge was made.
  return (
    <div
      className="checkout-banner checkout-banner--cancel"
      role="status"
      data-testid="checkout-cancel"
    >
      <p className="checkout-banner__detail">Checkout canceled — no charge was made.</p>
      <button
        type="button"
        className="checkout-banner__dismiss"
        data-testid="checkout-cancel-dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
      >
        ×
      </button>
    </div>
  );
}
